import React, { useEffect, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { TeamSearchResult } from '@askporter/client-grieg-lyric';
import { API } from '../../api';
import { TeamAutocompleteProps, TeamAutocomplete } from '.';

export interface ManagedStateMultipleTeamAutocompleteProps
  extends Omit<Extract<TeamAutocompleteProps, { isMultiple: true }>, 'value' | 'isMultiple'> {
  selectedTeamUids: string[];
}

/**
 * A TeamAutocomplete component with its own managed state
 * it fetches the selected teams on page load, using selectedTeamUids array
 */
export const ManagedStateMultipleTeamAutocomplete: React.FC<ManagedStateMultipleTeamAutocompleteProps> = ({
  selectedTeamUids = [],
  ...props
}) => {
  const [selectedTeams, setSelectedTeams] = useState<TeamSearchResult[]>([]);

  const results = useQueries({
    queries: selectedTeamUids.map((uid) => ({
      queryKey: ['get-team-on-page-load', { selectedTeamUid: uid }],
      queryFn: () => API().get({ path: `teams/${uid}` }) as Promise<TeamSearchResult>,
      // We only want to make the request on page load as the autocomplete won't have the users associated with the uids from the url
      enabled: Boolean(selectedTeamUids.length !== selectedTeams.length),
    })),
  });

  useEffect(() => {
    results.forEach((result) => {
      if (result.data && !selectedTeams.find((team) => team.uid === result.data.uid)) {
        setSelectedTeams((prev) => [...prev, result.data]);
      }
    });
  }, [results, selectedTeams]);

  return (
    <TeamAutocomplete
      {...props}
      isMultiple
      value={selectedTeams}
      onChange={(value) => {
        setSelectedTeams(value);
        props.onChange(value);
      }}
    />
  );
};
